import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import FormikContainer from "../../../../components/form/FormikContainer.jsx";
import DeleteConfirmationModal from "../../../../components/modal/DeleteConfirmationModal.jsx";
import genericTranslations from "../../../../utils/genericTranslations.js";
import { selectFolderById } from "../../selectors.js";
import { fetchFolderById, removeFolder, saveFolder } from "../../thunks.js";

import FolderForm from "./FolderForm.jsx";

const messages = defineMessages({
  deleteWarning: {
    id: "modules.ArriVideo.components.FolderEditor.deleteWarning",
    description: "No remove label",
    defaultMessage: "You can't delete a folder which is not empty.",
  },
  deleteFolder: {
    id: "modules.ArriVideo.components.FolderEditor.deleteFolder",
    description: "Delete folder label",
    defaultMessage: "Delete folder",
  },
  deleteFolderConfirmation: {
    id: "modules.ArriVideo.components.FolderEditor.deleteFolderConfirmation",
    description: "Delete folder label",
    defaultMessage: "Are you sure you want to remove this folder?",
  },
});

const FolderEditor = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { identifier } = useParams();

  const [error, setError] = useState(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const folder = useSelector((state) =>
    selectFolderById(state, { identifier }),
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchFolderById(identifier));
      } catch (err) {
        setError(err);
      }
    };

    if (identifier && !folder) {
      fetchData();
    }
  }, [folder, identifier, dispatch]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    try {
      await dispatch(saveFolder(values, folder ? folder.id : undefined));
      navigate("/arrivideo#folders", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const validate = (values) => {
    const errors = {};
    if (!values.title) {
      errors.title = intl.formatMessage(genericTranslations.required);
    }
    return errors;
  };

  const handleRemoveSoftwarePackage = async () => {
    try {
      await dispatch(removeFolder(folder.id));
      navigate("/arrivideo#folders", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    }
  };

  if (error) {
    return (
      <strong>
        {intl.formatMessage(genericTranslations.errorMessage)}{" "}
        {error.toString()}
      </strong>
    );
  }

  if (!folder && identifier) {
    return <strong>{intl.formatMessage(messages.notFoundMessage)}</strong>;
  }

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        Map {folder ? "bewerken" : "aanmaken"}
      </Card.Header>
      <Card.Body>
        <FormikContainer
          initialValues={folder || {}}
          onSubmit={handleSubmit}
          validate={validate}
          formComponent={FolderForm}
          extraProps={{
            onClickDelete: () => {
              setShowDeleteModal(true);
            },
          }}
        />
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={intl.formatMessage(messages.deleteFolder)}
          bodyText={intl.formatMessage(messages.deleteFolderConfirmation)}
          confirmButtonText={intl.formatMessage(genericTranslations.delete)}
          cancelButtonText={intl.formatMessage(genericTranslations.close)}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleRemoveSoftwarePackage}
        />
      </Card.Body>
    </Card>
  );
};

export default FolderEditor;
